/**
 * Why a figure or a list is not available, and the ONE action that fixes it
 * (Pascal's rule, 27/09/2026: « quand la source est à synchroniser mais qu'il
 * n'y a pas de bouton, que fait l'utilisateur ? »).
 *
 * Every state that is not « à jour » names its real cause and carries a
 * single action that executes or opens exactly what has to be corrected:
 * synchronise the source, open the deals or invoices concerned, open the
 * setting. A valid value whose older history was purged is an information and
 * carries no action. The vocabulary mirrors the shared metric contract
 * (`metric_contract.metrics[].cause / status_label / reason / action`), so a
 * contract action coming from the server renders with the same button.
 *
 * Pure module: nothing here renders; FixAction.tsx draws the buttons.
 */
import { safeString, toNumber } from '../../lib/format';
import type { ViewStateFilters, ViewStateKey } from '../../store/ui';
import { toFacturesTarget, toIntegrationsTarget, type BusinessOsNavigationTarget } from './navigation';

export type SourceConnector = 'hubspot' | 'qonto' | 'digiforma';

export const CONNECTOR_LABEL: Record<SourceConnector, string> = {
  hubspot: 'HubSpot',
  qonto: 'Qonto',
  digiforma: 'Digiforma',
};

export function isSourceConnector(value: unknown): value is SourceConnector {
  return value === 'hubspot' || value === 'qonto' || value === 'digiforma';
}

/** Causes of the shared metric contract ('' = available). */
export type FixCause =
  | ''
  | 'source_not_synced'
  | 'source_error'
  | 'source_stale'
  | 'coverage_incomplete'
  | 'configuration_missing'
  | 'entry_missing'
  | 'history_limited';

export const CAUSE_STATUS_LABEL: Record<FixCause, string> = {
  '': 'À jour',
  source_not_synced: 'Source à synchroniser',
  source_error: 'Synchronisation en échec',
  source_stale: 'Copie ancienne',
  coverage_incomplete: 'Couverture incomplète',
  configuration_missing: 'À configurer',
  entry_missing: 'Saisie manquante',
  history_limited: 'Exercice complet · historique limité',
};

/**
 * The single action of a state. `sync` runs the synchronisation when the user
 * holds `sync_integrations`, otherwise opens THAT connector; `navigate` opens
 * the items or the setting concerned; `external` opens the record in the tool
 * where it is corrected (HubSpot, Digiforma, Qonto).
 */
export type FixAction =
  | { kind: 'sync'; label: string; connector: SourceConnector }
  | { kind: 'navigate'; label: string; target: BusinessOsNavigationTarget }
  | { kind: 'external'; label: string; href: string };

export interface SourceFix {
  cause: FixCause;
  statusLabel: string;
  /** One precise, true sentence (never generic, never « ou »). */
  reason: string;
  action: FixAction | null;
}

export const SOURCE_OK: SourceFix = Object.freeze({ cause: '', statusLabel: CAUSE_STATUS_LABEL[''], reason: '', action: null }) as SourceFix;

export function syncAction(connector: SourceConnector): FixAction {
  return { kind: 'sync', label: `Synchroniser ${CONNECTOR_LABEL[connector]}`, connector };
}

export function connectorTarget(connector: SourceConnector): BusinessOsNavigationTarget {
  return toIntegrationsTarget({ connector });
}

/* ───────────────────────── Targets agreed with the metric contract ───────────────────────── */

/** Problems the Pipeline filters with `?probleme=` (open HubSpot deals). */
export type PipelineProblem = 'hors-exercice' | 'sans-date' | 'sans-montant' | 'devise' | 'sans-probabilite';

export const PIPELINE_PROBLEMS: readonly PipelineProblem[] = ['hors-exercice', 'sans-date', 'sans-montant', 'devise', 'sans-probabilite'];

export function parsePipelineProblem(raw: unknown): PipelineProblem | null {
  const value = safeString(raw).trim().toLowerCase();
  return (PIPELINE_PROBLEMS as readonly string[]).includes(value) ? (value as PipelineProblem) : null;
}

export function pipelineProblemTarget(problem: PipelineProblem): BusinessOsNavigationTarget {
  return { route: `/pipeline?probleme=${problem}`, view: 'pipeline', filters: {} };
}

/** Factures scope listing the Qonto invoices whose date, amount or currency is missing. */
export const INVOICE_SCOPE_INCOMPLETE = 'incomplete';

export function incompleteInvoicesTarget(): BusinessOsNavigationTarget {
  // Every status: an incomplete invoice is often a draft, which the default
  // working perimeter (paid, unpaid, partially paid) would hide.
  return toFacturesTarget({ flag: INVOICE_SCOPE_INCOMPLETE, status: 'all' });
}

/** Setting of the internal day cost (finance page, admin only to edit). */
export const INTERNAL_DAY_COST_TARGET: BusinessOsNavigationTarget = {
  route: '/finance-ops?reglage=cout-interne',
  view: 'finance_ops',
  filters: {},
};

/** The planning with the Excel paste panel open, where the realised is entered. */
export const REALISED_PASTE_TARGET: BusinessOsNavigationTarget = {
  route: '/affectation?coller=1',
  view: 'affectation',
  filters: {},
};

/** Panel « Rapprochements d'identités » pre-filtered on the Qonto invoices. */
export function qontoInvoiceLinksTarget(search = ''): BusinessOsNavigationTarget {
  return toIntegrationsTarget({ connector: 'qonto', linkType: 'client_invoice', search });
}

/* ───────────────────────── Diagnosis of a source copy ───────────────────────── */

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function pct(value: number): string {
  return `${(Math.round(value * 10) / 10).toLocaleString('fr-FR')} %`;
}

/** « 1er septembre », « 26 septembre ». */
function syncDay(raw: unknown): string {
  const text = safeString(raw);
  if (!text) return '';
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  const day = parsed.getDate();
  return `${day === 1 ? '1er' : day} ${parsed.toLocaleDateString('fr-FR', { month: 'long' })}`;
}

function counted(count: number, singular: string, plural: string): string {
  return `${count.toLocaleString('fr-FR')} ${count > 1 ? plural : singular}`;
}

function fix(cause: FixCause, reason: string, action: FixAction | null, statusLabel = CAUSE_STATUS_LABEL[cause]): SourceFix {
  return { cause, statusLabel, reason, action };
}

export interface DiagnoseOptions {
  year?: number;
  /** Open HubSpot deals dated outside the exercise, when the caller knows it better than the meta. */
  outsideYearCount?: number | null;
}

/**
 * A HubSpot portal identity that a synchronisation cannot fix, as the metric
 * contract states it (ops_overview `hubspot_portal_unconfigured` /
 * `hubspot_portal_mismatch`): no AIRCS portal expected, or another portal
 * connected. Any other unverified portal (`recheck_pending`) is re-verified
 * by the next synchronisation (integration_sync.hubspot_projection_portal_status).
 */
function hubspotPortalConfigurationFix(meta: Record<string, unknown>): SourceFix | null {
  switch (safeString(meta.portal_identity_status).trim().toLowerCase()) {
    case 'unconfigured':
      return fix(
        'configuration_missing',
        'Aucun portail HubSpot AIRCS attendu n’est configuré : les affaires HubSpot ne sont pas lues.',
        { kind: 'navigate', label: 'Configurer la connexion HubSpot', target: connectorTarget('hubspot') },
      );
    case 'mismatch':
      return fix(
        'configuration_missing',
        'Le portail HubSpot connecté n’est pas le portail AIRCS attendu : ses affaires sont masquées.',
        { kind: 'navigate', label: 'Ouvrir la connexion HubSpot', target: connectorTarget('hubspot') },
      );
    default:
      return null;
  }
}

function hubspotReadinessFix(meta: Record<string, unknown>, readiness: string, options: DiagnoseOptions): SourceFix | null {
  const year = options.year;
  const exercise = year ? `l’exercice ${year}` : 'l’exercice';
  switch (readiness) {
    case 'blocked_portal_unverified':
      // Same cause and action as the metric contract: the copy is not yet
      // proven to come from the AIRCS portal, and a synchronisation re-verifies it.
      return hubspotPortalConfigurationFix(meta) || fix(
        'source_not_synced',
        'La copie HubSpot n’est pas encore prouvée issue du portail AIRCS : une synchronisation la revérifie.',
        syncAction('hubspot'),
      );
    case 'blocked_open_deals_outside_year': {
      const count = toNumber(options.outsideYearCount ?? meta.open_deals_outside_year_count);
      return fix(
        'coverage_incomplete',
        count > 0
          ? `${counted(count, 'affaire ouverte est datée', 'affaires ouvertes sont datées')} hors de ${exercise} : le pipeline ne peut pas être additionné.`
          : `Des affaires ouvertes sont datées hors de ${exercise} : le pipeline ne peut pas être additionné.`,
        {
          kind: 'navigate',
          label: count > 1 ? `Ouvrir les ${count.toLocaleString('fr-FR')} affaires` : count === 1 ? 'Ouvrir l’affaire' : 'Ouvrir les affaires hors exercice',
          target: pipelineProblemTarget('hors-exercice'),
        },
      );
    }
    case 'blocked_non_eur_or_missing_currency': {
      const coverage = optionalNumber(meta.currency_eur_coverage_pct);
      return fix(
        'coverage_incomplete',
        coverage === null
          ? 'La devise EUR n’est pas confirmée sur toutes les affaires ouvertes : leurs montants ne sont pas additionnés.'
          : `La devise EUR n’est confirmée que sur ${pct(coverage)} des affaires ouvertes : leurs montants ne sont pas additionnés.`,
        { kind: 'navigate', label: 'Voir les affaires sans devise EUR', target: pipelineProblemTarget('devise') },
      );
    }
    case 'blocked_missing_amounts': {
      const coverage = optionalNumber(meta.amount_coverage_pct);
      return fix(
        'coverage_incomplete',
        coverage === null
          ? 'Des affaires ouvertes n’ont pas de montant dans HubSpot : le pipeline n’est pas additionné.'
          : `Seules ${pct(coverage)} des affaires ouvertes ont un montant dans HubSpot : le pipeline n’est pas additionné.`,
        { kind: 'navigate', label: 'Voir les affaires sans montant', target: pipelineProblemTarget('sans-montant') },
      );
    }
    case 'blocked_missing_close_dates': {
      const coverage = optionalNumber(meta.close_date_coverage_pct);
      return fix(
        'coverage_incomplete',
        coverage === null
          ? `Des affaires ouvertes n’ont pas de date de clôture : elles ne se rattachent pas à ${exercise}.`
          : `Seules ${pct(coverage)} des affaires ouvertes ont une date de clôture : les autres ne se rattachent pas à ${exercise}.`,
        { kind: 'navigate', label: 'Voir les affaires sans date', target: pipelineProblemTarget('sans-date') },
      );
    }
    case 'blocked_missing_probabilities': {
      const coverage = optionalNumber(meta.probability_coverage_pct);
      return fix(
        'coverage_incomplete',
        coverage === null
          ? 'Des affaires ouvertes n’ont pas de probabilité : le pipeline pondéré n’est pas calculé.'
          : `Seules ${pct(coverage)} des affaires ouvertes ont une probabilité : le pipeline pondéré n’est pas calculé.`,
        { kind: 'navigate', label: 'Voir les affaires sans probabilité', target: pipelineProblemTarget('sans-probabilite') },
      );
    }
    default:
      return null;
  }
}

function qontoReadinessFix(meta: Record<string, unknown>, readiness: string): SourceFix | null {
  const incomplete: FixAction = { kind: 'navigate', label: 'Voir les factures incomplètes', target: incompleteInvoicesTarget() };
  switch (readiness) {
    case 'blocked_missing_issue_dates':
      return fix('coverage_incomplete', 'Des factures Qonto n’ont pas de date d’émission : les totaux de l’exercice sont masqués.', incomplete);
    case 'blocked_missing_amounts':
      return fix('coverage_incomplete', 'Des factures Qonto n’ont pas de montant HT : les totaux sont masqués.', incomplete);
    case 'blocked_non_eur_or_missing_currency': {
      const coverage = optionalNumber(meta.currency_eur_coverage_pct);
      return fix(
        'coverage_incomplete',
        coverage === null
          ? 'La devise EUR n’est pas confirmée sur toutes les factures Qonto : les totaux sont masqués.'
          : `La devise EUR n’est confirmée que sur ${pct(coverage)} des factures Qonto : les totaux sont masqués.`,
        incomplete,
      );
    }
    default:
      return null;
  }
}

/**
 * The real state of one source copy (HubSpot deals, Qonto invoices, Digiforma
 * sessions), from its `source_meta`, with the action that fixes it.
 */
export function diagnoseSource(connector: SourceConnector, rawMeta: unknown, options: DiagnoseOptions = {}): SourceFix {
  const label = CONNECTOR_LABEL[connector];
  const meta = asRecord(rawMeta);
  const sync = syncAction(connector);
  if (!meta) return fix('source_not_synced', `${label} n’a encore jamais été synchronisé : rien n’est compté.`, sync);
  // Server order (metric contract): a portal that is not configured or not
  // the AIRCS one comes first, since no synchronisation can fix it.
  const portal = connector === 'hubspot' ? hubspotPortalConfigurationFix(meta) : null;
  if (portal) return portal;
  const freshness = safeString(meta.freshness).toLowerCase();
  const lastSuccess = safeString(meta.last_success_at);
  if (safeString(meta.last_error) || freshness === 'degraded') {
    return fix('source_error', `La dernière synchronisation ${label} a échoué : la copie n’est pas exploitable.`, sync);
  }
  if (freshness === 'never_synced' || freshness === 'unavailable' || (!lastSuccess && freshness !== 'fresh')) {
    return fix('source_not_synced', `${label} n’a encore jamais été synchronisé : rien n’est compté.`, sync);
  }
  if (freshness === 'stale') {
    const day = syncDay(lastSuccess);
    return fix('source_stale', day ? `La copie ${label} date du ${day} : elle est trop ancienne pour être comptée.` : `La copie ${label} est trop ancienne pour être comptée.`, sync);
  }
  const readiness = safeString(meta.business_readiness);
  if (readiness.startsWith('blocked')) {
    const specific = connector === 'hubspot'
      ? hubspotReadinessFix(meta, readiness, options)
      : connector === 'qonto' ? qontoReadinessFix(meta, readiness) : null;
    if (specific) return specific;
    return fix(
      'coverage_incomplete',
      safeString(meta.quality_warning) || `Des données ${label} sont incomplètes : les totaux sont masqués.`,
      { kind: 'navigate', label: `Ouvrir le connecteur ${label}`, target: connectorTarget(connector) },
    );
  }
  const purged = toNumber(meta.retention_filtered_count);
  if (meta.historical_financial_complete === false || purged > 0) {
    if (meta.selected_period_financial_complete === true || readiness === 'ready_selected_period_historical_retention') {
      return fix('history_limited', options.year
        ? `L’exercice ${options.year} est complet ; seul l’historique plus ancien a été purgé.`
        : 'L’exercice affiché est complet ; seul l’historique plus ancien a été purgé.', null);
    }
    return fix(
      'history_limited',
      `${label} a purgé ${purged > 0 ? counted(purged, 'élément terminé ancien', 'éléments terminés anciens') : 'des éléments terminés anciens'} : les totaux historiques sont masqués, les éléments ouverts restent complets.`,
      null,
      'Historique limité',
    );
  }
  return SOURCE_OK;
}

/* ───────────────────────── Qonto attribution (clients, missions, invoices) ───────────────────────── */

/**
 * Why the Qonto amounts per client or per mission are not computed, from
 * `financial_attribution.status`, with the action that unblocks them.
 */
export function qontoAttributionFix(status: unknown, year?: number): SourceFix {
  const exercise = year ? `l’exercice ${year}` : 'l’exercice affiché';
  const sync = syncAction('qonto');
  const incomplete: FixAction = { kind: 'navigate', label: 'Voir les factures incomplètes', target: incompleteInvoicesTarget() };
  switch (safeString(status)) {
    case '':
    case 'available':
      return SOURCE_OK;
    case 'qonto_not_synchronized':
    case 'qonto_snapshot_unavailable':
      return fix('source_not_synced', 'Qonto n’a encore jamais été synchronisé : aucune facture n’est rattachée.', sync);
    case 'qonto_snapshot_stale':
      return fix('source_stale', 'La copie Qonto est trop ancienne : les montants rattachés sont masqués jusqu’à la prochaine synchronisation.', sync);
    case 'qonto_snapshot_degraded':
      return fix('source_error', 'La dernière synchronisation Qonto a échoué : les montants rattachés sont masqués.', sync);
    case 'qonto_scope_mismatch':
      return fix('source_not_synced', `La copie Qonto ne couvre pas ${exercise} : synchronisez-la pour cet exercice.`, sync);
    case 'partial_historical_retention':
      return fix('history_limited', `Des factures terminées de ${exercise} ont été purgées de la copie Qonto : les montants rattachés de l’exercice sont masqués.`, null, 'Historique limité');
    case 'qonto_financial_snapshot_incomplete':
      return fix('coverage_incomplete', 'Des factures Qonto n’ont pas de montant HT ou de date d’émission : aucun montant n’est rattaché.', incomplete);
    case 'qonto_currency_unverified':
      return fix('coverage_incomplete', 'La devise EUR n’est pas confirmée sur toutes les factures Qonto : aucun montant n’est rattaché.', incomplete);
    default:
      return fix(
        'source_error',
        'Le rattachement Qonto n’a pas pu être calculé.',
        { kind: 'navigate', label: 'Ouvrir le connecteur Qonto', target: connectorTarget('qonto') },
      );
  }
}

/* ───────────────────────── Metric contract (server) → the same SourceFix ───────────────────────── */

/** In-app routes a contract action may open, and the view state each one owns. */
const CONTRACT_ROUTE_VIEWS: Record<string, ViewStateKey> = {
  '/dashboard': 'dashboard',
  '/ma-semaine': 'actions',
  '/finance-ops': 'finance_ops',
  '/pipeline': 'pipeline',
  '/missions': 'missions',
  '/affectation': 'affectation',
  '/calendrier': 'calendrier',
  '/clients': 'clients',
  '/intervenants': 'intervenants',
  '/factures': 'factures',
  '/data-quality': 'data_quality',
  '/integrations': 'integrations',
  '/client-360': 'client360',
  '/ai': 'ai_insights',
};

const CONTRACT_FILTER_KEYS = [
  'search', 'status', 'category', 'flag', 'scope', 'monthKey', 'demandDays', 'entityId',
  'resourceId', 'owner', 'severity', 'connector', 'entityType', 'segment',
] as const;

function contractTarget(raw: unknown): BusinessOsNavigationTarget | null {
  const input = asRecord(raw);
  if (!input) return null;
  const route = safeString(input.route).trim();
  // In-app routes only: never another origin (« //host », « https: »).
  if (!/^\/[a-z0-9-]/i.test(route)) return null;
  const path = route.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const requested = safeString(input.view).trim();
  const known = Object.values(CONTRACT_ROUTE_VIEWS) as string[];
  const view = (known.includes(requested) ? requested : CONTRACT_ROUTE_VIEWS[path]) as ViewStateKey | undefined;
  if (!view) return null;
  const rawFilters = asRecord(input.filters) || {};
  const filters: ViewStateFilters = {};
  for (const key of CONTRACT_FILTER_KEYS) {
    const value = rawFilters[key];
    if (typeof value === 'string' && value.trim()) filters[key] = value.trim();
  }
  const target: BusinessOsNavigationTarget = { route, view, filters };
  const year = Number(input.year);
  if (Number.isInteger(year) && year > 0) target.year = year;
  return target;
}

/** The action of a contract item, as the same FixAction the screens render. */
export function contractFixAction(raw: unknown): FixAction | null {
  const action = asRecord(raw);
  if (!action) return null;
  const label = safeString(action.label).trim();
  const kind = safeString(action.kind).trim();
  if (kind === 'sync') {
    const connector = safeString(action.connector).trim().toLowerCase();
    if (!isSourceConnector(connector)) return null;
    return { kind: 'sync', label: label || syncAction(connector).label, connector };
  }
  if (kind !== 'navigate' || !label) return null;
  const target = contractTarget(action.target);
  return target ? { kind: 'navigate', label, target } : null;
}

const CONTRACT_CAUSES = new Set<string>(Object.keys(CAUSE_STATUS_LABEL));

/**
 * A metric of `metric_contract.metrics` (build_metric_contract_payload) as a
 * SourceFix: its real cause, its pill label, its one sentence and its one
 * action, exactly as the Data-quality screen shows them. Null when the metric
 * is available, or when the server predates the contract (no known cause):
 * the caller then diagnoses the source copy itself.
 */
export function fixFromContract(item: unknown): SourceFix | null {
  const record = asRecord(item);
  if (!record) return null;
  const cause = safeString(record.cause).trim();
  if (!cause || !CONTRACT_CAUSES.has(cause)) return null;
  const reason = safeString(record.reason).trim();
  if (!reason) return null;
  const typed = cause as FixCause;
  // The contract carries no action for an information (history_limited).
  const action = typed === 'history_limited' ? null : contractFixAction(record.action);
  return fix(typed, reason, action, safeString(record.status_label).trim() || CAUSE_STATUS_LABEL[typed]);
}

/** The contract item of `key` (« billed_revenue_ht »…) turned into its SourceFix, if any. */
export function contractMetricFix(contract: unknown, key: string): SourceFix | null {
  const metrics = asRecord(contract)?.metrics;
  if (!Array.isArray(metrics)) return null;
  const item = metrics.find((candidate) => safeString(asRecord(candidate)?.key) === key);
  return item ? fixFromContract(item) : null;
}

/* ───────────────────────── HubSpot trust signals (Ma semaine, tableau de bord) ───────────────────────── */

export interface ReadinessTask {
  title: string;
  detail: string;
  actionLabel: string;
  ariaLabel?: string;
  target: BusinessOsNavigationTarget;
  /**
   * Set when the button runs the synchronisation of this connector (render it
   * with SyncSourceButton); `target` is then THAT connector, which the button
   * opens for a user without the right to synchronise.
   */
  sync?: SourceConnector;
}

function leadingNumber(text: unknown): number | null {
  const match = /(\d+(?:[.,]\d+)?)/.exec(safeString(text));
  return match ? Number(match[1].replace(',', '.')) : null;
}

/**
 * The server signal `hubspot-<readiness>` rewritten as a task whose button
 * opens exactly the deals to correct (`/pipeline?probleme=…`). The server
 * meta carries the count or the coverage (« 3 deal(s) ouvert(s) à redater »,
 * « Couverture EUR: 80% »); its wording is never printed as is. The HubSpot
 * copy's `source_meta`, when the caller has it, tells an unverified portal
 * that a synchronisation re-verifies from one that must be configured.
 */
export function hubspotReadinessTask(readiness: string, meta: unknown, sourceMeta?: unknown): ReadinessTask {
  const value = leadingNumber(meta);
  const coverage = value === null ? '' : pct(value);
  switch (readiness) {
    case 'blocked_open_deals_outside_year': {
      const count = value === null ? 0 : Math.round(value);
      return {
        title: count ? `Corriger ${count} date${count > 1 ? 's' : ''} de clôture` : 'Corriger les dates de clôture',
        detail: 'Affaires ouvertes à la clôture dépassée dans HubSpot',
        actionLabel: count > 1 ? `Ouvrir les ${count}` : count === 1 ? 'Ouvrir l’affaire' : 'Ouvrir les affaires à redater',
        ariaLabel: count > 1 ? `Ouvrir les ${count} affaires à redater dans le pipeline` : count === 1 ? 'Ouvrir l’affaire à redater dans le pipeline' : undefined,
        target: pipelineProblemTarget('hors-exercice'),
      };
    }
    case 'blocked_non_eur_or_missing_currency':
      return {
        title: 'Confirmer la devise EUR des affaires',
        detail: coverage ? `Devise EUR confirmée sur ${coverage} des affaires ouvertes` : 'Devise EUR non confirmée dans HubSpot',
        actionLabel: 'Voir les affaires sans devise EUR',
        target: pipelineProblemTarget('devise'),
      };
    case 'blocked_missing_close_dates':
      return {
        title: 'Dater la clôture des affaires',
        detail: coverage ? `Dates de clôture renseignées sur ${coverage} des affaires ouvertes` : 'Dates de clôture manquantes dans HubSpot',
        actionLabel: 'Voir les affaires sans date',
        target: pipelineProblemTarget('sans-date'),
      };
    case 'blocked_missing_probabilities':
      return {
        title: 'Renseigner les probabilités des affaires',
        detail: coverage ? `Probabilités renseignées sur ${coverage} des affaires ouvertes` : 'Probabilités manquantes dans HubSpot',
        actionLabel: 'Voir les affaires sans probabilité',
        target: pipelineProblemTarget('sans-probabilite'),
      };
    case 'blocked_portal_unverified': {
      // Same cause and action as the metric contract and diagnoseSource().
      const configuration = hubspotPortalConfigurationFix(asRecord(sourceMeta) || {});
      if (configuration?.action?.kind === 'navigate') {
        return {
          title: 'Portail HubSpot à configurer',
          detail: configuration.reason,
          actionLabel: configuration.action.label,
          target: configuration.action.target,
        };
      }
      return {
        title: 'Portail HubSpot à revérifier',
        detail: 'Copie HubSpot pas encore prouvée issue du portail AIRCS : ses affaires ne sont pas comptées',
        actionLabel: syncAction('hubspot').label,
        target: connectorTarget('hubspot'),
        sync: 'hubspot',
      };
    }
    default:
      return {
        title: 'Renseigner les montants des affaires',
        detail: coverage ? `Montants renseignés sur ${coverage} des affaires ouvertes` : 'Montants manquants dans HubSpot',
        actionLabel: 'Voir les affaires sans montant',
        target: pipelineProblemTarget('sans-montant'),
      };
  }
}

/** « HubSpot non synchronisé », « Données Qonto incomplètes »… — the title of a source-backed state. */
export function sourceStateTitle(connector: SourceConnector, diagnosis: SourceFix): string {
  const label = CONNECTOR_LABEL[connector];
  switch (diagnosis.cause) {
    case 'source_error': return `Synchronisation ${label} en échec`;
    case 'source_stale': return `Copie ${label} ancienne`;
    case 'coverage_incomplete': return `Données ${label} incomplètes`;
    case 'configuration_missing': return `${label} à configurer`;
    case 'history_limited': return diagnosis.statusLabel;
    case 'entry_missing': return 'Saisie manquante';
    case '': return `${label} à jour`;
    default: return `${label} non synchronisé`;
  }
}
